import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import { CardComponent } from "@chargebee/chargebee-js-react-wrapper";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import chargebeeService from '../services/chargebee.service';
import AuthService from '../services/auth.service';
import { Roles } from '../types/enums';
import moment from 'moment';
import { validateEmail, validateAndSanitizeName, sanitizeTextInput } from '../utils/common.util';
import { EVENT_TYPES, logEvent } from '../utils/events.util';
import {LeadsquaredService} from '../services/leadsquared/leadsquared.service';
interface PurchaseModalProps {
  open: boolean;
  onClose: () => void;
  planTitle: string;
  planPrice: string;
  planType: 'professionalBundle' | 'auditBundle';
  isNonAttendee: boolean;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  open,
  onClose,
  planTitle,
  planPrice,
  planType,
  isNonAttendee,
}) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [isCardValid, setIsCardValid] = useState(false);
  const [isCardReady, setIsCardReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Processing...');
  const [cardToken, setCardToken] = useState<string>("");
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [isEmailValidating, setIsEmailValidating] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const cardRef = useRef<any>(null);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Clear form errors when modal is closed
  const handleClose = () => {
    setFormErrors({ name: '', email: '', password: '' });
    setEmailExists(false);
    setIsEmailValidating(false);
    onClose();
  };

  // Email validation function for professional bundle
  const validateEmailExists = async (email: string) => {
    if (planType !== 'professionalBundle' || !email.trim()) {
      return;
    }

    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      return; // Don't validate if email format is invalid
    }

    try {
      setIsEmailValidating(true);
      setEmailExists(false);
      
      const response = await AuthService.checkEmailExists(email);
      
      if (response.data?.exists) {
        setEmailExists(true);
        setFormErrors(prev => ({ 
          ...prev, 
          email: 'This email already exists. Please use a different email address.' 
        }));
      } else {
        setEmailExists(false);
        setFormErrors(prev => ({ 
          ...prev, 
          email: '' 
        }));
      }
    } catch (error) {
      console.error('Error checking email existence:', error);
      // Don't show error to user for API failures, just log it
    } finally {
      setIsEmailValidating(false);
    }
  };

  // Password validation function following existing patterns
  const validatePassword = (password: string): string => {
    const minLength = 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    if (!hasUppercase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowercase) {
      return 'Password must contain at least one lowercase letter';
    }
    return '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation for name field with hyperlink injection protection
    if (name === 'name' && value.trim()) {
      const nameValidation = validateAndSanitizeName(value, 'Full name');
      if (!nameValidation.isValid) {
        setFormErrors(prev => ({
          ...prev,
          [name]: nameValidation.error || 'Invalid name provided'
        }));
        return;
      }
    }

    // Clear error when user starts typing
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Real-time validation for email
    if (name === 'email') {
      if (!value.trim()) {
        setFormErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else {
        const emailValidation = validateEmail(value);
        if (!emailValidation.isValid) {
          setFormErrors(prev => ({ ...prev, email: emailValidation.error || 'Please enter a valid email' }));
        }
      }
    } else if (name === 'password') {
      if (!value.trim()) {
        setFormErrors(prev => ({ ...prev, password: 'Password is required' }));
      } else {
        const passwordError = validatePassword(value);
        if (passwordError) {
          setFormErrors(prev => ({ ...prev, password: passwordError }));
        }
      }
    }
  };

  // Helper function to split name into firstName and lastName with security sanitization
  const splitName = (fullName: string) => {
    // Security: Sanitize the full name before splitting
    const sanitizedFullName = sanitizeTextInput(fullName.trim());
    const nameParts = sanitizedFullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    return { firstName, lastName };
  };

  const getTimezoneString = () => {
    const timezone = moment.tz.guess() || "UTC";
    const offset = moment.tz(timezone).format("Z");
    const zoneName = moment.tz(timezone).zoneName();
    return `(GMT${offset}),${timezone},${zoneName}`;
};

  const handleCompletePurchase = async () => {
    // Validate form fields with comprehensive validation
    const errors: { [key: string]: string } = {};

    // Validate name with hyperlink injection protection
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    } else {
      const nameValidation = validateAndSanitizeName(formData.name, 'Full name');
      if (!nameValidation.isValid) {
        errors.name = nameValidation.error || 'Invalid name provided';
      }
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error || 'Please enter a valid email';
      }
    }

    // Only require password for non-audit bundle plans
    if (planType !== 'auditBundle') {
      if (!formData.password.trim()) {
        errors.password = 'Password is required';
      } else {
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
          errors.password = passwordError;
        }
      }
    }

    // Check if email validation is in progress for professional bundle
    if (planType === 'professionalBundle' && isEmailValidating) {
      toast.error('Please wait for email validation to complete.');
      return;
    }

    // Check if email exists for professional bundle
    if (planType === 'professionalBundle' && emailExists) {
      toast.error('This email already exists. Please use a different email address.');
      return;
    }

    // Set errors and return if validation fails
    if (Object.keys(errors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...errors }));
      toast.error('Please fix the validation errors before proceeding');
      return;
    }

    if (!isCardValid || !cardRef.current) {
      toast.error('Please complete card details before proceeding');
      return;
    }

    try {
      setIsLoading(true);
      setCardError(null);

      // Tokenize the card
      const result = await cardRef.current.tokenize();

      if (result.error) {
        setCardError(result.error.message || 'Card tokenization failed');
        toast.error('Please check your card details and try again');
        return;
      }

      setCardToken(result.token);
      console.log('Card tokenized successfully:', result.token);
      
      // Handle different purchase flows based on planType
      switch (planType) {
        case 'professionalBundle':
          await handleProfessionalBundlePurchase(result.token);
          break;
        case 'auditBundle':
          await handleAuditBundlePurchase(result.token);
          break;
      }

      setLoadingText('Processing...');
      
    } catch (error: any) {
      setLoadingText('Processing...');
      console.error('Card tokenization error:', error);
      setCardError('Failed to process card. Please try again.');
      toast.error('Failed to process card. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Professional Bundle Purchase Handler
  const handleProfessionalBundlePurchase = async (token: string) => {
    logEvent(formData.email, EVENT_TYPES.BUNDLE_PURCHASE, {
      bundleType: 'professionalBundle',
      name: formData.name,
      email: formData.email,
      planId: 'All-In-One-USD-Yearly',
      planUnits: 500,
      step: 'start'
    });

    try {
      console.log('Processing Professional Bundle purchase with token:', token);
      
      const { firstName, lastName } = splitName(formData.name);
      
      // Check if email exists
      setLoadingText('Verifying email...');
      const emailCheckResponse = await AuthService.checkEmailExists(formData.email);
      
      if (emailCheckResponse.data?.exists) {
        // User exists - follow existing user flow
        console.log('User exists, processing charge for existing user');
        setLoadingText('Processing payment...');

        const chargeDataProfessionalBundle = {
          email: formData.email,
          cardToken: token,
          planId: 'All-In-One-USD-Yearly',
          planUnits: 500,
        };

        const changedPlanResponse = await chargebeeService.changePlanByEmail(chargeDataProfessionalBundle);

        logEvent(formData.email, EVENT_TYPES.BUNDLE_PURCHASE, {
          bundleType: 'professionalBundle',
          name: formData.name,
          email: formData.email,
          planId: 'All-In-One-USD-Yearly',
          planUnits: 500,
          amount: 1297,
          flow: 'existingUser',
          step: 'success',
          response: changedPlanResponse
        });

        try {
          const leadSquaredId = await LeadsquaredService.createtLead({
            email: formData.email,
            firstName: formData.name,
            lastName: ""
          }, "", "");
          
            LeadsquaredService.joinActivity({
              id: leadSquaredId,
              activityEvent: 205,
              activityValue: "Professional + Audit Combo",
              activityNote: `Workshop Purchase`
            });
            if(!isNonAttendee){
              LeadsquaredService.joinActivity({
                id: leadSquaredId,
                activityEvent: 205,
                activityValue: "Fast Action Bonus",
                activityNote: `Workshop Purchase`
              });
            }
            if(changedPlanResponse){
              const isUpgrade = changedPlanResponse.subscription.oldPlanId !== 'All-In-One-USD-Yearly';
              LeadsquaredService.joinActivity({
                id: leadSquaredId,
                activityEvent: 205,
                activityValue: isUpgrade ? 'Upgraded' : 'Downgraded',
                activityNote: `Workshop Purchase`
              });
            }
          
        } catch (error) {
          console.error('Error tracking Leadsquared activity for Professional Bundle:', error);
        }

        toast.success('Professional Bundle purchased successfully!');
        handleClose();
        // Navigate to login page after successful professional bundle purchase
        navigate('/login');
      } else {
        // User doesn't exist - signup the user first
        console.log('User does not exist, creating new user account');
        setLoadingText('Signing up...');
        
        // Create signup data
        const signupData = {
          email: formData.email,
          password: formData.password,
          firstName,
          lastName,
          phoneNumber: "",
          timezone: getTimezoneString(),
          planId: 'All-In-One-USD-Yearly',
          planUnits: 500,
          couponId: 'PROFESSIONALBUNDLE-2',
          cardToken: token,
          trialEnd: 0
        };

        // Signup the user
        const signupResponse = await AuthService.signup(signupData);
        console.log('User signup response:', signupResponse);

        logEvent(formData.email, EVENT_TYPES.BUNDLE_PURCHASE, {
          bundleType: 'professionalBundle',
          name: formData.name,
          email: formData.email,
          planId: 'All-In-One-USD-Yearly',
          planUnits: 500,
          amount: 1297,
          flow: 'newUser',
          step: 'success',
          response: signupResponse?.data
        });

        try {
            const LsId = await LeadsquaredService.createtLead({
              email: formData.email,
              firstName: formData.name,
              lastName: ""
            }, "", "");
            LeadsquaredService.joinActivity({
              id: LsId,
              activityEvent: 205,
              activityValue: "Professional + Audit Combo",
              activityNote: `Workshop Purchase`
            });
            if(!isNonAttendee){
              LeadsquaredService.joinActivity({
                id: LsId,
                activityEvent: 205,
                activityValue: "Fast Action Bonus",
                activityNote: `Workshop Purchase`
              });
            }
        } catch (error) {
          console.error('Error tracking Leadsquared activity for Professional Bundle (new user):', error);
        }
        
        toast.success('Account created and Professional Bundle purchased successfully!');
        // Navigate to login page after successful professional bundle purchase
        navigate('/login');
      }
      
      // Close modal after successful purchase
      handleClose();
      
    } catch (error: any) {
      logEvent(formData.email, EVENT_TYPES.BUNDLE_PURCHASE, {
        bundleType: 'professionalBundle',
        name: formData.name,
        email: formData.email,
        planId: 'All-In-One-USD-Yearly',
        planUnits: 500,
        step: 'error',
        error: error.message,
        response: error.response
      });

      console.error('Professional Bundle purchase error:', error);
      if (error.message?.includes('already exists')) {
        toast.error('An account with this email already exists. Please use a different email or try logging in.');
      } else {
        toast.error('Failed to complete Professional Bundle purchase. Please try again.');
      }
      throw error;
    }
  };

  // Audit Bundle Purchase Handler
  const handleAuditBundlePurchase = async (token: string) => {
    try {
      logEvent(formData.email, EVENT_TYPES.BUNDLE_PURCHASE, {
        bundleType: 'auditBundle',
        name: formData.name,
        email: formData.email,
        planId: 'Audit-Bundle-USD',
        planUnits: 1,
        step: 'start'
      });

      console.log('Processing Audit Bundle purchase with token:', token);
      
      const { firstName, lastName } = splitName(formData.name);
      
      const chargeData = {
        firstName,
        lastName,
        email: formData.email,
        itemPriceId: 'Audit-Bundle-USD', // Update this based on your actual item price ID
        cardToken: token
      };

      const response = await chargebeeService.chargeCustomer(chargeData);
      console.log('Audit Bundle purchase response:', response);

      logEvent(formData.email, EVENT_TYPES.BUNDLE_PURCHASE, {
        bundleType: 'auditBundle',
        name: formData.name,
        email: formData.email,
        planId: 'Audit-Bundle-USD',
        planUnits: 1,
        step: 'success',
        amount: (response?.data?.charge?.amount || 0) / 100,
        paymentStatus: response?.data?.charge?.status,
        isNewCustomer: response?.data?.customer?.isNewCustomer,
        response: response?.data
      });

      try {
        const leadSquaredId = await LeadsquaredService.createtLead({
          email: formData.email,
          firstName: formData.name,
          lastName: ""
        }, "", "");
        
            LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Audit Bundle",
            activityNote: `Workshop Purchase`
            });
            if(!isNonAttendee){
              LeadsquaredService.joinActivity({
                id: leadSquaredId,
                activityEvent: 205,
                activityValue: "Fast Action Bonus",
                activityNote: `Workshop Purchase`
              });
            }
      } catch (error) {
        console.error('Error tracking Leadsquared activity for Audit Bundle:', error);
      }
      
      // Download invoice if available
      if (response?.data?.charge?.invoiceId) {
        try {
          const invoiceId = response.data.charge.invoiceId;
          await downloadInvoice(invoiceId);
          toast.success('Audit Bundle purchased successfully! Invoice downloaded.');
        } catch (downloadError) {
          console.error('Failed to download invoice:', downloadError);
          toast.success('Audit Bundle purchased successfully!');
        }
      } else {
        toast.success('Audit Bundle purchased successfully!');
      }
      
      // Close modal after successful purchase
      handleClose();
      // Navigate to external confirmation page after successful audit bundle purchase
      const confirmationUrl = "https://get.easywebinar.com/payment-success"
      window.location.href = confirmationUrl;
      
    } catch (error: any) {
      console.error('Audit Bundle purchase error:', error);
      logEvent(formData.email, EVENT_TYPES.BUNDLE_PURCHASE, {
        bundleType: 'auditBundle',
        name: formData.name,
        email: formData.email,
        planId: 'Audit-Bundle-USD',
        planUnits: 1,
        step: 'error',
        error: error.message,
        response: error.response
      });
      toast.error('Failed to complete Audit Bundle purchase. Please try again.');
      throw error;
    }
  };

  const downloadInvoice = async (invoiceId: string) => {
    const blob = await chargebeeService.downloadInvoice(invoiceId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoiceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };


  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: '16px',
          p: { xs: 2, md: 4 },
          maxWidth: { xs: '90vw', sm: '500px' },
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={isLoading ? undefined : handleClose}
          disabled={isLoading}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: isLoading ? '#D1D5DB' : '#6B7280',
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <Close />
        </IconButton>

        {/* Heading */}
        <Box sx={{ mb: { xs: 3, md: 4 }, mt: { xs: 0.5, md: 1 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#2563EB',
              fontSize: { xs: '18px', sm: '22px', md: '28px' },
              fontWeight: 600,
              mb: { xs: 0.25, md: 0.5 },
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
           {planTitle || 'Unlock Your Webinar Funnel Audit Bundle today!'} 
          </Typography>
        </Box>

        {/* Form Fields */}
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            name="name"
            placeholder="Full Name"
            variant="outlined"
            fullWidth
            size="medium"
            value={formData.name}
            onChange={handleInputChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: formErrors.name ? '#ef4444' : '#D1D5DB',
                },
                '&:hover fieldset': {
                  borderColor: formErrors.name ? '#ef4444' : '#9CA3AF',
                },
                '&.Mui-focused fieldset': {
                  borderColor: formErrors.name ? '#ef4444' : '#2563EB',
                },
              },
            }}
          />
          <TextField
            name="email"
            placeholder="Email"
            variant="outlined"
            fullWidth
            size="medium"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={(e) => validateEmailExists(e.target.value)}
            error={!!formErrors.email}
            helperText={formErrors.email}
            InputProps={{
              endAdornment: isEmailValidating && planType === 'professionalBundle' ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: formErrors.email ? '#ef4444' : '#D1D5DB',
                },
                '&:hover fieldset': {
                  borderColor: formErrors.email ? '#ef4444' : '#9CA3AF',
                },
                '&.Mui-focused fieldset': {
                  borderColor: formErrors.email ? '#ef4444' : '#2563EB',
                },
              },
            }}
          />
          {planType !== 'auditBundle' && (
            <TextField
              name="password"
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              autoComplete="new-password"
              fullWidth
              size="medium"
              value={formData.password}
              onChange={handleInputChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: formErrors.password ? '#ef4444' : '#D1D5DB',
                  },
                  '&:hover fieldset': {
                    borderColor: formErrors.password ? '#ef4444' : '#9CA3AF',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: formErrors.password ? '#ef4444' : '#2563EB',
                  },
                },
              }}
            />
          )}

          {/* Credit Card Field */}
          <Box width="100%">
            <Typography color="#333333" fontSize="14px" fontWeight="500" mb="12px">
              Card Details
            </Typography>
            <Box width="100%" sx={{
              position: 'relative',
              '& .CbHosted': {
                borderRadius: '3px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                padding: '14px 16px',
                '&:hover, &--focus': {
                  borderColor: '#666666',
                }
              }
            }}>
              <CardComponent
                ref={cardRef}
                currency="USD"
                showTestCards={true}
                styles={{
                  border: "1px solid #666666",
                  base: {
                    padding: "14px 16px",
                    border: "1px solid #666666",
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    color: '#333333',
                    '::placeholder': {
                      color: '#667085',
                    },
                  },
                  invalid: {
                    color: '#ff0000',
                  },
                }}
                onChange={(status: any) => {
                  console.log('Card status:', status);

                  if (status.error) {
                    setCardError(status.error.message);
                    setIsCardValid(false);
                  } else if (!status.complete) {
                    // Clear error but indicate card is not complete
                    setCardError(null);
                    setIsCardValid(false);
                  } else {
                    // Card is complete and valid
                    setCardError(null);
                    setIsCardValid(true);
                  }
                }}
                onReady={() => {
                  console.log('Card component ready');
                  setIsCardReady(true);
                  setIsCardValid(false); // Reset validity when component is ready
                }}
                onBlur={() => {
                  console.log('Card component blurred');
                }}
                onFocus={() => {
                  console.log('Card component focused');
                }}
              />
            </Box>
            {cardError && (
              <Typography color="error" fontSize="12px" mt={1}>
                {cardError}
              </Typography>
            )}
          </Box>
        </Box>

        {/* CTA Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!isCardValid || isLoading || (planType === 'professionalBundle' && (emailExists || isEmailValidating))}
          onClick={handleCompletePurchase}
          sx={{
            bgcolor: '#FF6B35',
            color: 'white',
            fontWeight: 600,
            borderRadius: '8px',
            mb: 4,
            minHeight: '48px',
            '&:hover': {
              bgcolor: '#E55A2B',
            },
            '&.Mui-disabled': {
              bgcolor: '#cccccc',
              color: '#666666'
            }
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
              {loadingText}
            </>
          ) : (
            `Complete Purchase Offer - ${planPrice}`
          )}
        </Button>

        {/* Footer Links */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flexWrap: 'wrap'
        }}>
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '10px',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            © 2025 All Rights Reserved
          </Typography>
          <Box
            sx={{
              width: '1px',
              height: '12px',
              backgroundColor: '#D1D5DB',
            }}
          />
          <a
            href="https://easywebinar.com/terms-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#6B7280',
              textDecoration: 'none',
              fontSize: '10px',
              fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
          >
            Terms of Service
          </a>
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '10px',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            •
          </Typography>
          <a
            href="https://easywebinar.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#6B7280',
              textDecoration: 'none',
              fontSize: '10px',
              fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
          >
            Privacy Policy
          </a>
        </Box>
      </Box>
    </Modal>
  );
};

export default PurchaseModal;

